import { useTheme } from 'next-themes'
import { Toaster as Sonner, toast } from 'sonner'
import type { ToasterProps } from 'sonner'
import { cn } from '@/lib/utils'
import {
  CheckCircle2,
  Info,
  AlertTriangle,
  AlertCircle,
  Loader2,
} from 'lucide-react'

/**
 * Figma (node 193:1386 — Sonner / State=Default):
 * Container: bg-popover, border border-border (1px), rounded-md (8px), p-4 (16px),
 *   shadow-[0px_4px_12px_0px_rgba(0,0,0,0.1)], flex gap-2 (8px) items-center, w-[336px]
 * Title: text-sm (14px) font-medium (500) leading-5 (20px) text-popover-foreground
 * Description: text-sm (14px) font-normal (400) leading-5 (20px) text-muted-foreground
 * Title+Desc gap: gap-[2px]
 * Icon: 20px (size-5), type-specific: Success=CheckCircle2, Info=Info,
 *   Warning=AlertTriangle, Error=AlertCircle, Loading=Loader2 (animated)
 * Action (primary): bg-primary text-primary-foreground h-6 px-2 rounded text-xs font-medium shadow-sm
 * Cancel (secondary): bg-secondary text-secondary-foreground h-6 px-2 rounded text-xs font-medium shadow-sm
 */

const ICON_CLASS = 'h-5 w-5'

const intuiToastIcons = {
  success: <CheckCircle2 className={ICON_CLASS} />,
  info: <Info className={ICON_CLASS} />,
  warning: <AlertTriangle className={ICON_CLASS} />,
  error: <AlertCircle className={ICON_CLASS} />,
  loading: <Loader2 className={cn(ICON_CLASS, 'animate-spin')} />,
}

export interface IntuiToasterProps extends Omit<ToasterProps, 'theme'> {
  className?: string
}

function IntuiToaster({ className, ...props }: IntuiToasterProps) {
  const { theme = 'system' } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className={cn('toaster group', className)}
      style={{ '--width': '336px' } as React.CSSProperties}
      icons={intuiToastIcons}
      toastOptions={{
        classNames: {
          toast: cn(
            'group toast',
            'group-[.toaster]:bg-popover group-[.toaster]:text-popover-foreground',
            'group-[.toaster]:border-border group-[.toaster]:shadow-[0px_4px_12px_0px_rgba(0,0,0,0.1)]',
            'group-[.toaster]:rounded-md group-[.toaster]:py-2.5 group-[.toaster]:px-4',
          ),
          title: 'group-[.toast]:text-sm group-[.toast]:font-medium group-[.toast]:leading-5',
          description:
            'group-[.toast]:text-sm group-[.toast]:font-normal group-[.toast]:leading-5 group-[.toast]:text-muted-foreground',
          actionButton: cn(
            'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
            'group-[.toast]:h-6 group-[.toast]:px-2 group-[.toast]:rounded',
            'group-[.toast]:text-xs group-[.toast]:font-medium group-[.toast]:shadow-sm',
          ),
          cancelButton: cn(
            'group-[.toast]:bg-secondary group-[.toast]:text-secondary-foreground',
            'group-[.toast]:h-6 group-[.toast]:px-2 group-[.toast]:rounded',
            'group-[.toast]:text-xs group-[.toast]:font-medium group-[.toast]:shadow-sm',
          ),
        },
      }}
      {...props}
    />
  )
}

export { IntuiToaster, toast, intuiToastIcons }
